import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import FormModal from "../components/FormModal";
import { Loading } from "../components/Loader";
import RoleCheckboxList from "../components/RoleCheckboxList";
import {
  createUser,
  fetchRoles,
  fetchUsers,
  selectRoles,
  selectUsers,
  suggestCredentials,
} from "../features/access/accessSlice";
import { applyRoleChange, fillEmptyCredentials, hasCashierRole } from "../features/access/cashierForm";
import { formatDateTime, fullName } from "../features/access/format";
import { selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";
import "./ManagementPages.css";

const EMPTY_FORM = {
  username: "",
  password: "",
  first_name: "",
  last_name: "",
  email: "",
  is_staff: true,
  roles: [],
};

/**
 * «Управление → Пользователи»: сотрудники CRM и кассы, их роли и доступ.
 * Фильтры живут в URL — так список переживает «назад» из карточки пользователя.
 */
export default function UsersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: me } = useSelector(selectAuth);
  const users = useSelector(selectUsers);
  const roles = useSelector(selectRoles);

  const search = searchParams.get("search") || "";
  const role = searchParams.get("role") || "";
  const status = searchParams.get("status") || "active";

  const [searchInput, setSearchInput] = useState(search);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  // Пароль сгенерирован — показываем открытым текстом: его нужно передать кассиру.
  const [generated, setGenerated] = useState(false);

  const setParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Поиск — с задержкой, чтобы не слать запрос на каждую букву.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput.trim() !== search) setParam("search", searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search, setParam]);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchUsers({
        search,
        ...(role === "none" ? { no_role: "true" } : { role }),
        is_active: status === "all" ? "" : String(status === "active"),
      }),
    );
  }, [dispatch, search, role, status]);

  const closeModal = useCallback(() => {
    setCreating(false);
    setForm(EMPTY_FORM);
    setFormError(null);
    setGenerated(false);
  }, []);

  /** Логин и пароль из цифр от бэкенда; `overwrite` — перезаписать и заполненные поля. */
  const generateCredentials = async (overwrite) => {
    const result = await dispatch(suggestCredentials());
    if (!suggestCredentials.fulfilled.match(result)) {
      setFormError(result.payload);
      return;
    }
    setForm((f) => (overwrite ? { ...f, ...result.payload } : fillEmptyCredentials(f, result.payload)));
    setGenerated(true);
  };

  const onRolesChange = (ids) => {
    const { form: next, needsCredentials } = applyRoleChange(form, ids, roleList);
    setForm(next);
    if (needsCredentials) generateCredentials(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    const result = await dispatch(createUser({ ...form, username: form.username.trim() }));
    setSaving(false);
    if (createUser.fulfilled.match(result)) {
      closeModal();
      navigate(`/management/users/${result.payload.id}`);
    } else {
      setFormError(result.payload);
    }
  };

  const roleList = Array.isArray(roles.data) ? roles.data : [];
  const cashier = hasCashierRole(form.roles, roleList);
  const list = Array.isArray(users.data) ? users.data : [];

  return (
    <div className="mgmt-page">
      <div className="mg-header">
        <div>
          <div className="mg-title">Пользователи</div>
          <div className="mg-subtitle">
            Сотрудники CRM и кассы. Права складываются из всех ролей пользователя. Удалить пользователя нельзя —
            его отключают, история чеков и смен сохраняется.
          </div>
        </div>
        {can(me, "user", "create") && (
          <div className="mg-actions">
            <button type="button" className="mg-btn mg-btn--primary" onClick={() => setCreating(true)}>
              + Пользователь
            </button>
          </div>
        )}
      </div>

      <div className="mg-body">
        <div className="mg-filters">
          <input
            className="mg-input mg-input--search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Поиск: логин, имя, email"
          />
          <select className="mg-select" value={role} onChange={(e) => setParam("role", e.target.value)}>
            <option value="">Все роли</option>
            {roleList.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
            <option value="none">Без роли</option>
          </select>
          <select className="mg-select" value={status} onChange={(e) => setParam("status", e.target.value)}>
            <option value="active">Активные</option>
            <option value="inactive">Отключённые</option>
            <option value="all">Все</option>
          </select>
        </div>

        {users.status === "loading" && !list.length && <Loading text="Загрузка пользователей…" />}
        {users.status === "failed" && <div className="mg-error">{users.error}</div>}

        <div className="mg-table-wrap">
          <table className="mg-table">
            <thead>
              <tr>
                <th>Логин</th>
                <th>ФИО</th>
                <th>Роли</th>
                <th>Статус</th>
                <th className="mg-hide-mobile">Последний вход</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && users.status === "succeeded" && (
                <tr>
                  <td colSpan={5} className="mg-empty">
                    Никого не нашли
                  </td>
                </tr>
              )}
              {list.map((u) => (
                <tr
                  key={u.id}
                  className="mg-row"
                  onClick={() => navigate(`/management/users/${u.id}`)}
                  title="Открыть пользователя"
                >
                  <td className="mg-mono">{u.username}</td>
                  <td>{fullName(u) || <span className="mg-muted">—</span>}</td>
                  <td>
                    <div className="mg-badges">
                      {u.is_superuser && <span className="mg-badge mg-badge--accent">суперпользователь</span>}
                      {u.roles.map((r) => (
                        <span key={r.id} className="mg-badge">
                          {r.label}
                        </span>
                      ))}
                      {!u.roles.length && !u.is_superuser && <span className="mg-muted">без роли</span>}
                      {u.has_overrides && (
                        <span className="mg-badge mg-badge--accent" title="Есть права сверх ролей или отнятые у ролей">
                          личные права
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="mg-badges">
                      {u.is_active ? (
                        <span className="mg-badge mg-badge--success">активен</span>
                      ) : (
                        <span className="mg-badge mg-badge--danger">отключён</span>
                      )}
                      {u.is_staff && <span className="mg-badge">CRM</span>}
                    </div>
                  </td>
                  <td className="mg-muted mg-hide-mobile">{formatDateTime(u.last_login)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal open={creating} title="Новый пользователь" onClose={closeModal}>
        <form className="mg-form" onSubmit={submit}>
          <label className="mg-field">
            <span className="mg-label">Логин</span>
            <input
              className="mg-input mg-mono"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              inputMode={cashier ? "numeric" : undefined}
              autoComplete="off"
              required
              autoFocus
            />
          </label>
          <label className="mg-field">
            <span className="mg-label">Пароль</span>
            <input
              className="mg-input mg-mono"
              type={generated ? "text" : "password"}
              value={form.password}
              inputMode={cashier ? "numeric" : undefined}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              minLength={6}
              required
            />
          </label>
          {(cashier || generated) && (
            <div className="mg-field mg-field--wide mg-credentials-note">
              {generated && (
                <span className="mg-field-hint">
                  Логин и пароль сгенерированы из цифр — для входа на кассовом терминале. Запишите пароль и
                  передайте кассиру: после создания он больше не показывается.
                </span>
              )}
              <button type="button" className="mg-btn mg-btn--small" onClick={() => generateCredentials(true)}>
                Сгенерировать логин и пароль
              </button>
            </div>
          )}
          <label className="mg-field">
            <span className="mg-label">Фамилия</span>
            <input
              className="mg-input"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </label>
          <label className="mg-field">
            <span className="mg-label">Имя</span>
            <input
              className="mg-input"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
          </label>
          <label className="mg-field mg-field--wide">
            <span className="mg-label">Email</span>
            <input
              className="mg-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <div className="mg-field mg-field--wide">
            <span className="mg-label">Роли</span>
            <RoleCheckboxList roles={roleList} value={form.roles} onChange={onRolesChange} />
          </div>
          <label className="mg-toggle mg-field--wide">
            <input
              type="checkbox"
              checked={form.is_staff}
              onChange={(e) => setForm({ ...form, is_staff: e.target.checked })}
            />
            <span>
              Доступ в CRM
              <span className="mg-role-desc">
                {cashier && !form.is_staff
                  ? "Снят автоматически: кассир работает только на кассовом терминале."
                  : "Без него сотрудник работает только на кассовом терминале."}
              </span>
            </span>
          </label>
          {formError && <div className="mg-error mg-field--wide">{formError}</div>}
          <div className="mg-form-actions mg-field--wide">
            <button type="button" className="mg-btn" onClick={closeModal}>
              Отмена
            </button>
            <button type="submit" className="mg-btn mg-btn--primary" disabled={saving}>
              {saving ? "Создаём…" : "Создать"}
            </button>
          </div>
        </form>
      </FormModal>
    </div>
  );
}
